import { formatCost } from './formatCost';
import { getCostRange } from './costHelpers';
import { countryToFlag } from './countryFlags';
import { CATEGORIES } from '../components/constants';

/**
 * Format a 24h time string ("09:00" or "14:30") to 12h ("9:00 AM" / "2:30 PM").
 * Returns null for falsy input or non-24h strings.
 */
function formatTime(time) {
  if (!time) return null;
  // Only format strings containing ":" (24h format like "09:00" or "14:30")
  if (!time.includes(':')) return null;
  const [hourStr, minuteStr] = time.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = minuteStr || '00';
  if (isNaN(hour)) return null;
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minute} ${period}`;
}

/**
 * Safely convert a value to a Date. Handles: Date, ISO string, Firestore Timestamp, null.
 */
function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value + (value.includes('T') ? '' : 'T00:00:00'));
  if (typeof value?.toDate === 'function') return value.toDate(); // Firestore Timestamp
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000); // raw Timestamp
  return null;
}

/**
 * Add a number of days to a date value, return a new Date.
 */
function addDays(dateValue, count) {
  const d = toDate(dateValue);
  if (!d) return new Date(); // fallback to today
  const result = new Date(d.getTime());
  result.setDate(result.getDate() + count);
  return result;
}

/**
 * Format a Date as "Monday, June 22, 2026".
 */
function formatDate(date) {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Time block sort order — activities use `time` as a descriptive block label
 * (e.g. "Morning", "Afternoon") rather than exact 24h times.
 */
const TIME_BLOCK_ORDER = {
  'early am': 0, 'morning': 1, 'midday': 2,
  'afternoon': 3, 'evening': 4, 'late night': 5,
};

/**
 * Sort activities by their `order` field, then by time block, nulls last.
 */
function sortActivities(activities) {
  return [...activities].sort((a, b) => {
    // Primary sort: explicit order field
    if (a.order != null && b.order != null) return a.order - b.order;
    if (a.order != null) return -1;
    if (b.order != null) return 1;
    // Fallback: time block label
    const aBlock = TIME_BLOCK_ORDER[(a.time || '').toLowerCase()] ?? 99;
    const bBlock = TIME_BLOCK_ORDER[(b.time || '').toLowerCase()] ?? 99;
    return aBlock - bBlock;
  });
}

/**
 * For transit days, borrow a field value from the nearest non-transit neighbor.
 * Looks ahead first (destination), then back (origin). Returns null if none found.
 */
function findNeighborField(sortedDays, index, fieldName) {
  for (let i = index + 1; i < sortedDays.length; i++) {
    if (!sortedDays[i].isTransitDay && sortedDays[i][fieldName]) {
      return sortedDays[i][fieldName];
    }
  }
  for (let i = index - 1; i >= 0; i--) {
    if (!sortedDays[i].isTransitDay && sortedDays[i][fieldName]) {
      return sortedDays[i][fieldName];
    }
  }
  return null;
}

/**
 * Prepare a clean, structured export object from raw trip state.
 *
 * @param {Object} tripData — trip document from Firestore
 * @param {Array} days — array of day documents (may be unsorted)
 * @param {Object} activitiesByDay — { dayId: [activity, ...] }
 * @param {Object} displayPrefs — { showCostEstimates, showBookingInfo, showLocation }
 * @returns {ExportData}
 */
export function prepareExportData(tripData, days, activitiesByDay, displayPrefs) {
  const { showCostEstimates, showBookingInfo, showLocation } = displayPrefs;

  const sortedDays = [...days].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const departureCity = (tripData.brief?.departureCity || '').trim().toLowerCase();

  let parsedStart = toDate(tripData.startDate);
  if (!parsedStart) {
    console.warn('[prepareExportData] startDate is missing or invalid, falling back to today');
    parsedStart = new Date();
  }
  const startDate = parsedStart.toISOString().split('T')[0];
  const endDate = addDays(parsedStart, sortedDays.length - 1).toISOString().split('T')[0];

  // Build unique city list for destination summary (excluding transit days)
  const seenCities = new Set();
  const uniqueCities = [];
  for (const day of sortedDays) {
    if (day.isTransitDay) continue;
    const city = day.city;
    if (city && !seenCities.has(city)) {
      seenCities.add(city);
      uniqueCities.push(city);
    }
  }

  const exportDays = sortedDays.map((day, index) => {
    const dayDate = addDays(parsedStart, index);
    const rawActivities = activitiesByDay[day.id] || [];
    const sorted = sortActivities(rawActivities);

    const activities = sorted.map((act) => {
      // Firestore uses `time` as a block label ("Morning", "Afternoon", etc.)
      // not 24h startTime/endTime. Use formatTime only if a 24h time exists.
      const startFormatted = formatTime(act.startTime);
      const endFormatted = formatTime(act.endTime);

      let timeRange = null;
      if (startFormatted && endFormatted) {
        timeRange = `${startFormatted} \u2013 ${endFormatted}`;
      } else if (startFormatted) {
        timeRange = startFormatted;
      } else if (act.time) {
        // Fallback: use the descriptive time block label directly
        timeRange = act.time;
      }

      const catEntry = CATEGORIES[act.category];
      const categoryLabel = catEntry ? catEntry.label : act.category || '';

      // Location is an object { name, address } in Firestore — flatten to string
      let locationStr = null;
      if (showLocation && act.location) {
        if (typeof act.location === 'string') {
          locationStr = act.location;
        } else {
          const parts = [act.location.name, act.location.address].filter(Boolean);
          locationStr = parts.join(', ') || null;
        }
      }

      const result = {
        title: act.title || '',
        detail: act.detail || '',
        startTime: startFormatted,
        endTime: endFormatted,
        timeRange,
        duration: act.duration || null,
        category: act.category || '',
        categoryLabel,
        costDisplay: showCostEstimates ? formatCost(act.cost) : null,
        costRange: showCostEstimates
          ? (act.costRange || getCostRange(act.cost))
          : null,
        location: locationStr,
        imageUrl: act.img || act.photo || null,  // Firestore field is `img` (or `photo`)
        notes: act.notes || null,
      };

      if (showBookingInfo) {
        result.isBooked = act.bookingStatus === 'booked';
        result.bookingRef = act.bookingConfirmationRef || null;
      }

      return result;
    });

    return {
      dayNumber: index + 1,
      date: formatDate(dayDate),
      city: day.city || '',
      countryFlag: countryToFlag(day.country, day.countryCode),
      theme: day.theme || null,
      tip: day.tip || null,
      climate: day.isTransitDay && departureCity && (day.city || '').trim().toLowerCase() === departureCity
        ? findNeighborField(sortedDays, index, 'climate')
        : (day.climate || null),
      heroImageUrl: day.isTransitDay && departureCity && (day.city || '').trim().toLowerCase() === departureCity
        ? findNeighborField(sortedDays, index, 'heroImg')
        : (day.heroImg || null),
      isTransitDay: !!day.isTransitDay,
      transitMode: day.transitMode || null,
      activities,
    };
  });

  return {
    trip: {
      title: tripData.title || '',
      startDate,
      endDate,
      travelers: tripData.brief?.travelers || 0,
      travelerNames: tripData.brief?.travelerNames || [],
      heroImageUrl: tripData.heroImg || tripData.heroImage || null,
      destinationSummary: uniqueCities.join(', '),
    },
    days: exportDays,
    metadata: {
      generatedAt: new Date().toISOString(),
      generatedBy: 'Wanderologie',
      appUrl: 'https://www.wanderologie.co',
    },
  };
}
